import React, { useEffect, useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
const SpaSavingsCalculator = () => {
  // State for slider values
  const [employees, setEmployees] = useState(50);
  const [drinksPerDay, setDrinksPerDay] = useState(2);
  const [currentCost, setCurrentCost] = useState(1.85);
  const [replacementRate, setReplacementRate] = useState(80);
  // State for calculated results
  const [annualSavings, setAnnualSavings] = useState({
    min: 0,
    max: 0
  });
  const [monthlySavings, setMonthlySavings] = useState({
    min: 0,
    max: 0
  });
  const [bottlesSaved, setBottlesSaved] = useState(0);
  const [co2Avoided, setCo2Avoided] = useState(0);
  // Constants
  const WORK_DAYS_PER_YEAR = 260; // 52 weeks × 5 days
  const SMARTSODA_COST_PER_DRINK = 0.3; // 30 cents per drink
  const BASE_MONTHLY_COST = 350; // $350 per month per 250 employees
  const EMPLOYEES_PER_MACHINE = 250;
  const CO2_PER_BOTTLE_KG = 0.082;
  // Calculate results when inputs change
  useEffect(() => {
    // Total annual drinks
    const totalDrinks = employees * drinksPerDay * WORK_DAYS_PER_YEAR;
    // Drinks replaced by SmartSoda
    const replacedDrinks = totalDrinks * (replacementRate / 100);
    // Current annual spend
    const currentAnnualSpend = totalDrinks * currentCost;
    // Calculate monthly subscription based on employee count
    // We need to scale up for every 250 employees (or fraction thereof)
    const machinesNeeded = Math.ceil(employees / EMPLOYEES_PER_MACHINE);
    const monthlySubscription = BASE_MONTHLY_COST * machinesNeeded;
    // SmartSoda annual cost (subscription + per-drink cost)
    const ssAnnualCost = monthlySubscription * 12 + replacedDrinks * SMARTSODA_COST_PER_DRINK;
    // Annual savings
    const savings = currentAnnualSpend - ssAnnualCost;
    // Add a range for min/max savings (±10%)
    const minSavings = Math.max(0, savings * 0.9);
    const maxSavings = savings * 1.1;
    // Monthly savings
    const minMonthlySavings = minSavings / 12;
    const maxMonthlySavings = maxSavings / 12;
    // Bottles saved
    const bottlesSaved = replacedDrinks;
    // CO2 avoided (kg)
    const co2Avoided = bottlesSaved * CO2_PER_BOTTLE_KG;
    // Update state
    setAnnualSavings({
      min: minSavings,
      max: maxSavings
    });
    setMonthlySavings({
      min: minMonthlySavings,
      max: maxMonthlySavings
    });
    setBottlesSaved(bottlesSaved);
    setCo2Avoided(co2Avoided);
  }, [employees, drinksPerDay, currentCost, replacementRate]);
  // Format currency
  const formatCurrency = value => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  // Format large numbers
  const formatNumber = value => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };
  return <section className="py-12 sm:py-16 bg-blue-50" id="savings-calculator" data-id="element-743">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-744">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg overflow-hidden" data-id="element-745">
          <div className="p-4 sm:p-6 md:p-8" data-id="element-746">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-navy-950 mb-2 sm:mb-3" data-id="element-747">
              Showroom ROI Calculator
            </h2>
            <p className="text-center text-gray-600 text-sm sm:text-base mb-6 sm:mb-8 max-w-2xl mx-auto" data-id="element-748">
              Get a ballpark estimate of your potential savings with SmartSoda.
              Adjust the parameters below to see how they affect your results.
            </p>
            <div className="space-y-8 mb-8" data-id="element-749">
              {/* Number of Guests per day */}
              <div className="space-y-3" data-id="element-750">
                <div className="flex justify-between items-center mb-1" data-id="element-751">
                  <label htmlFor="employees" className="block text-sm font-medium text-gray-700" data-id="element-752">
                    Number of Guests per day
                  </label>
                </div>
                <div className="relative mt-1" data-id="element-753">
                  <div className="flex items-center" data-id="element-754">
                    <span className="text-xs text-gray-500 w-8" data-id="element-755">10</span>
                    <div className="relative flex-grow mx-2" data-id="element-756">
                      <input type="range" id="employees" min="10" max="500" step="10" value={employees} onChange={e => setEmployees(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-757" />
                      <div className="absolute top-0 transform -translate-y-full -translate-x-1/2 pointer-events-none" style={{
                      left: `${(employees - 10) / (500 - 10) * 100}%`,
                      top: '-24px' // Increased space to avoid overlap
                    }} data-id="element-758">
                        <div className="bg-blue-500 text-white font-bold py-1 px-3 rounded-full shadow-md" data-id="element-759">
                          {employees}
                        </div>
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-500 mx-auto" data-id="element-760"></div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 w-8 text-right" data-id="element-761">
                      500
                    </span>
                  </div>
                </div>
              </div>
              {/* Drinks Per Day */}
              <div className="space-y-3" data-id="element-762">
                <div className="flex justify-between items-center mb-1" data-id="element-763">
                  <label htmlFor="drinksPerDay" className="block text-sm font-medium text-gray-700" data-id="element-764">
                    Avg. Drinks Per Guest Per Day
                  </label>
                </div>
                <div className="relative mt-1" data-id="element-765">
                  <div className="flex items-center" data-id="element-766">
                    <span className="text-xs text-gray-500 w-8" data-id="element-767">1</span>
                    <div className="relative flex-grow mx-2" data-id="element-768">
                      <input type="range" id="drinksPerDay" min="1" max="5" step="0.5" value={drinksPerDay} onChange={e => setDrinksPerDay(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-769" />
                      <div className="absolute top-0 transform -translate-y-full -translate-x-1/2 pointer-events-none" style={{
                      left: `${(drinksPerDay - 1) / (5 - 1) * 100}%`,
                      top: '-24px' // Increased space to avoid overlap
                    }} data-id="element-770">
                        <div className="bg-blue-500 text-white font-bold py-1 px-3 rounded-full shadow-md" data-id="element-771">
                          {drinksPerDay}
                        </div>
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-500 mx-auto" data-id="element-772"></div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 w-8 text-right" data-id="element-773">
                      5
                    </span>
                  </div>
                </div>
              </div>
              {/* Current Cost Per Drink */}
              <div className="space-y-3" data-id="element-774">
                <div className="flex justify-between items-center mb-1" data-id="element-775">
                  <label htmlFor="currentCost" className="block text-sm font-medium text-gray-700" data-id="element-776">
                    Current Cost Per Drink ($)
                  </label>
                </div>
                <div className="relative mt-1" data-id="element-777">
                  <div className="flex items-center" data-id="element-778">
                    <span className="text-xs text-gray-500 w-8" data-id="element-779">$0.50</span>
                    <div className="relative flex-grow mx-2" data-id="element-780">
                      <input type="range" id="currentCost" min="0.5" max="3" step="0.05" value={currentCost} onChange={e => setCurrentCost(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-781" />
                      <div className="absolute top-0 transform -translate-y-full -translate-x-1/2 pointer-events-none" style={{
                      left: `${(currentCost - 0.5) / (3 - 0.5) * 100}%`,
                      top: '-24px' // Increased space to avoid overlap
                    }} data-id="element-782">
                        <div className="bg-blue-500 text-white font-bold py-1 px-3 rounded-full shadow-md" data-id="element-783">
                          ${currentCost.toFixed(2)}
                        </div>
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-500 mx-auto" data-id="element-784"></div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 w-12 text-right" data-id="element-785">
                      $3.00
                    </span>
                  </div>
                </div>
              </div>
              {/* Replacement Rate */}
              <div className="space-y-3" data-id="element-786">
                <div className="flex justify-between items-center mb-1" data-id="element-787">
                  <label htmlFor="replacementRate" className="block text-sm font-medium text-gray-700" data-id="element-788">
                    % of Drinks Replaced by SmartSoda
                  </label>
                </div>
                <div className="relative mt-1" data-id="element-789">
                  <div className="flex items-center" data-id="element-790">
                    <span className="text-xs text-gray-500 w-8" data-id="element-791">50%</span>
                    <div className="relative flex-grow mx-2" data-id="element-792">
                      <input type="range" id="replacementRate" min="50" max="100" step="5" value={replacementRate} onChange={e => setReplacementRate(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-793" />
                      <div className="absolute top-0 transform -translate-y-full -translate-x-1/2 pointer-events-none" style={{
                      left: `${(replacementRate - 50) / (100 - 50) * 100}%`,
                      top: '-24px' // Increased space to avoid overlap
                    }} data-id="element-794">
                        <div className="bg-blue-500 text-white font-bold py-1 px-3 rounded-full shadow-md" data-id="element-795">
                          {replacementRate}%
                        </div>
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-500 mx-auto" data-id="element-796"></div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 w-12 text-right" data-id="element-797">
                      100%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Results Section */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl shadow-inner mb-8" data-id="element-798">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-799">
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md transform transition-transform hover:scale-105" data-id="element-800">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-801">
                    Estimated Annual Savings
                  </h3>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600 mb-2 break-words" data-id="element-802">
                    {formatCurrency(annualSavings.min)}-
                    <br className="sm:hidden" data-id="element-803" />
                    {formatCurrency(annualSavings.max)}
                  </p>
                  <p className="text-xs sm:text-sm font-medium text-blue-500" data-id="element-804">
                    Monthly: {formatCurrency(monthlySavings.min)}-
                    {formatCurrency(monthlySavings.max)}
                  </p>
                </div>
                <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md transform transition-transform hover:scale-105" data-id="element-805">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-806">
                    Environmental Impact
                  </h3>
                  <div className="space-y-2" data-id="element-807">
                    <p className="text-xl sm:text-2xl font-bold text-green-600" data-id="element-808">
                      {formatNumber(bottlesSaved)}
                    </p>
                    <p className="text-xs sm:text-sm font-medium text-green-600" data-id="element-809">
                      plastic bottles eliminated annually
                    </p>
                    <p className="text-base sm:text-lg font-semibold text-green-600 mt-2" data-id="element-810">
                      {formatNumber(co2Avoided)} kg CO₂
                    </p>
                    <p className="text-xs sm:text-sm font-medium text-green-600" data-id="element-811">
                      carbon emissions avoided
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-600 italic text-center mt-4" data-id="element-812">
                *Estimates are subject to proposal and may vary based on
                specific requirements, seasonality, and availability.
              </p>
            </div>
            {/* CTA Button */}
            <div className="text-center" data-id="element-813">
              <a href="#book-demo" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-lg text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all transform hover:scale-105" data-id="element-814">
                Request Detailed Proposal
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-815" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default SpaSavingsCalculator;